import { createContext, useEffect, useState } from 'react';
import { setServerTimeOffset } from '../lib/dayjs';

export const ServerTimeContext = createContext(null);

export const ServerTimeProvider = ({ children }) => {
  const [offset, setOffset] = useState(0); // offset = serverTime - localTime
  const [isFetched, setIsFetched] = useState(false);

  useEffect(() => {
    // Skip server time sync on public routes
    const path = window.location.pathname;
    const isPublicRoute = path === '/' || path === '/login-register' || path.startsWith('/verify/');

    if (isPublicRoute) {
      setIsFetched(true);
      return;
    }

    const fetchServerTime = async () => {
      try {
        const startTime = Date.now();
        const baseUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000';
        const response = await fetch(`${baseUrl}/current-time`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const textData = await response.text();

        let responseData = textData;
        try {
          responseData = JSON.parse(textData);
        } catch {
          // Keep as string if parsing fails
        }

        // Handle cases where the response is a raw string or an object
        const serverTimeStr =
          typeof responseData === 'string' ? responseData : responseData.currentTime;

        if (!serverTimeStr) {
          throw new Error('Invalid server time response format');
        }

        const serverTime = new Date(serverTimeStr).getTime();

        if (isNaN(serverTime)) {
          throw new Error(`Failed to parse server time string: ${serverTimeStr}`);
        }
        const endTime = Date.now();

        // Simple latency correction: assume half the round-trip time
        const latency = (endTime - startTime) / 2;
        const currentServerTime = serverTime + latency;

        const calculatedOffset = currentServerTime - endTime;
        setOffset(calculatedOffset);
        setServerTimeOffset(calculatedOffset); // Sync global dayjs wrapper
        setIsFetched(true);
      } catch (error) {
        console.error('Failed to fetch server time:', error);
      }
    };

    fetchServerTime();
  }, []);

  const getSyncedTime = () => {
    return new Date(Date.now() + offset);
  };

  return (
    <ServerTimeContext.Provider value={{ getSyncedTime, isFetched, offset }}>
      {children}
    </ServerTimeContext.Provider>
  );
};
